import { ref } from 'vue';

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' renders the confirm button in danger-red — use for destructive actions. */
  severity?: 'danger' | 'warning' | 'info';
}

interface ConfirmRequest extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

// Module-level state — a single shared confirm slot. Stacking confirms would
// make the call-site ergonomics awkward; if a second showConfirm fires while
// one is open, the older one resolves to false (treated as cancelled).
const current = ref<ConfirmRequest | null>(null);

export const showConfirm = (options: ConfirmOptions): Promise<boolean> => {
  if (current.value) {
    current.value.resolve(false);
  }
  return new Promise<boolean>((resolve) => {
    current.value = { ...options, resolve };
  });
};

const settle = (result: boolean) => {
  if (!current.value) return;
  current.value.resolve(result);
  current.value = null;
};

export const useConfirm = () => ({
  current,
  showConfirm,
  acceptConfirm: () => settle(true),
  cancelConfirm: () => settle(false),
});
